import type { SchemaAnalysis, SchemaKeyChange, SchemaOperation, SchemaTableChange } from '../api';
import { plural } from '../labels';
import { StatusBadge } from './ui';

/**
 * DatabaseSchemaChanges (observation 1 follow-up): the exact structural
 * change of a Magento release, table by table, from comparing the live
 * release's declarative schema with this release's. Data patches are not
 * here: they are listed as data changes beside it.
 */

const opWord: Record<SchemaOperation, string> = {
  create: 'Add',
  drop: 'Remove',
  keep: 'Stays',
  modify: 'Change',
};

const tableOpWord: Record<SchemaOperation, string> = {
  create: 'New table',
  drop: 'Table removed',
  keep: 'Stays in the database',
  modify: 'Changed',
};

function summaryLine(s: NonNullable<SchemaAnalysis['diff']>['summary']): string {
  const parts: string[] = [];
  if (s.tables_created) parts.push(plural(s.tables_created, 'table created', 'tables created'));
  if (s.tables_dropped) parts.push(plural(s.tables_dropped, 'table removed', 'tables removed'));
  if (s.columns_added) parts.push(plural(s.columns_added, 'column added', 'columns added'));
  if (s.columns_dropped) parts.push(plural(s.columns_dropped, 'column removed', 'columns removed'));
  if (s.columns_modified) parts.push(plural(s.columns_modified, 'column changed', 'columns changed'));
  if (s.indexes_changed) parts.push(plural(s.indexes_changed, 'index changed', 'indexes changed'));
  if (s.constraints_changed) parts.push(plural(s.constraints_changed, 'key changed', 'keys changed'));
  return parts.length > 0 ? parts.join(', ') : 'No structural change';
}

function KeyRows({ keys, label }: { keys: SchemaKeyChange[]; label: string }) {
  if (keys.length === 0) return null;
  return (
    <>
      {keys.map((k) => (
        <li key={`${label}-${k.name}`} className={`schema-change schema-change--${k.operation}`}>
          <span className="schema-change__op">{opWord[k.operation]}</span>
          <span className="schema-change__what">
            {label} <code translate="no">{k.name}</code>
            {k.operation === 'modify' ? (
              <span className="schema-change__detail">
                {' '}
                {k.before} → {k.after}
              </span>
            ) : (
              <span className="schema-change__detail"> {k.after ?? k.before}</span>
            )}
          </span>
        </li>
      ))}
    </>
  );
}

function TableRows({ table }: { table: SchemaTableChange }) {
  const created = table.operation === 'create';
  return (
    <ul className="schema-changes__list">
      {created ? (
        <li className="schema-change schema-change--create">
          <span className="schema-change__op">Add</span>
          <span className="schema-change__what">
            {plural(table.columns.length, 'column')}, {plural(table.indexes.length, 'index', 'indexes')}, {plural(table.constraints.length, 'key')}
            {table.data_from ? <span className="schema-change__detail"> — copies data from {table.data_from}</span> : null}
          </span>
        </li>
      ) : null}
      {table.columns.map((c) => (
        <li key={`c-${c.name}`} className={`schema-change schema-change--${c.operation}`}>
          <span className="schema-change__op">{opWord[c.operation]}</span>
          <span className="schema-change__what">
            column <code translate="no">{c.name}</code>
            {c.operation === 'modify' ? (
              <span className="schema-change__detail">
                {' '}
                {c.before} → {c.after}
              </span>
            ) : c.operation === 'keep' ? (
              <span className="schema-change__detail"> no longer declared, but not whitelisted: Magento leaves it in place</span>
            ) : (
              <span className="schema-change__detail"> {c.after ?? c.before}</span>
            )}
            {c.data_from ? <span className="schema-change__detail"> — copies data from {c.data_from}</span> : null}
          </span>
        </li>
      ))}
      <KeyRows keys={table.indexes} label="index" />
      <KeyRows keys={table.constraints} label="key" />
      {table.attributes.map((a) => (
        <li key={`a-${a.name}`} className="schema-change schema-change--modify">
          <span className="schema-change__op">Change</span>
          <span className="schema-change__what">
            table {a.name}
            <span className="schema-change__detail">
              {' '}
              {a.before || 'default'} → {a.after || 'default'}
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export function DatabaseSchemaChanges({ schema }: { schema: SchemaAnalysis }) {
  const diff = schema.diff;
  if (schema.state !== 'compared' || !diff) return null;
  const changing = diff.tables.filter((t) => t.operation !== 'keep' || t.columns.length > 0);
  if (changing.length === 0) {
    return (
      <p className="panel__note" data-testid="schema-changes-none">
        <strong>No schema changes</strong> · live release <code translate="no">{schema.source_release}</code>
      </p>
    );
  }
  // A handful of tables open; a long list starts folded.
  const openByDefault = changing.length <= 3;
  return (
    <section className="schema-changes" aria-labelledby="schema-changes-title" data-testid="schema-changes">
      <div className="schema-changes__head">
        <h4 id="schema-changes-title" className="schema-changes__title">
          Schema changes
        </h4>
        <StatusBadge kind="schemaImpact" value={diff.impact} />
      </div>
      <p className="panel__note">
        {summaryLine(diff.summary)} · live <code translate="no">{schema.source_release}</code> →{' '}
        <code translate="no">{schema.target_release}</code>
      </p>
      <ul className="schema-changes__tables">
        {changing.map((t) => (
          <li key={t.table} data-testid={`schema-table-${t.table}`}>
            <details className="schema-table" open={openByDefault}>
              <summary>
                <code translate="no" className="schema-table__name">
                  {t.table}
                </code>
                <span className="schema-table__op">{tableOpWord[t.operation]}</span>
                {t.impact !== 'none' ? <StatusBadge kind="schemaImpact" value={t.impact} /> : null}
                <span className="schema-table__modules">{t.modules.join(', ')}</span>
              </summary>
              <TableRows table={t} />
            </details>
          </li>
        ))}
      </ul>
      {diff.modules_enabled.length > 0 || diff.modules_disabled.length > 0 ? (
        <p className="panel__note">
          {diff.modules_enabled.length > 0 ? <>Newly enabled: {diff.modules_enabled.join(', ')}. </> : null}
          {diff.modules_disabled.length > 0 ? <>No longer enabled: {diff.modules_disabled.join(', ')}.</> : null}
        </p>
      ) : null}
    </section>
  );
}

/** Sentences for the "What this cannot tell" list from the comparison's state. */
export function schemaUnknowns(schema: SchemaAnalysis | undefined): string[] {
  if (!schema) {
    return ['Which tables, columns and indexes change: this deployment was analysed before Commerce Ops compared release schemas.'];
  }
  const out: string[] = [];
  switch (schema.state) {
    case 'no_live_release':
      out.push('Which tables, columns and indexes change: no release is live on the server yet, so there is nothing to compare with.');
      break;
    case 'unreadable':
      out.push(`Which tables, columns and indexes change: the schema comparison could not read a release (${schema.reason ?? 'no reason given'}).`);
      break;
    case 'not_available':
      out.push('Which tables, columns and indexes change: the agent on this server is older than the schema comparison (0.3.4).');
      break;
    case 'not_run':
      out.push('Which tables, columns and indexes change: the deployment ended before the schema comparison ran.');
      break;
  }
  if (schema.live_matches_record === false) {
    out.push(
      `The server serves ${schema.source_release}, not the release Commerce Ops recorded as live; the comparison uses what the server serves.`,
    );
  }
  for (const note of schema.diff?.notes ?? []) out.push(note);
  return out;
}
